import React, { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Drawer from "../components/Drawer";
import {
  Segmented, SelectFilter, TierMeter, TierBars, CountLabel, NoResults, CloseButton,
} from "../components/Bits";
import {
  USERS, PERSONA_TAG_CLASS, PERSONA_TYPE_LABEL, USER_STATUS_META,
  PERSONA_FILTERS, TIER_FILTERS,
} from "../data/mockData";

const maxTierOf = (user) => Math.max(...user.personas.map((p) => p.tier));

/** Users — list with persona/tier filters, plus the slide-over detail. */
const Users = () => {
  const { search } = useOutletContext();
  const [personaFilter, setPersonaFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [openUserId, setOpenUserId] = useState(null);
  const [activeTab, setActiveTab] = useState(null);

  const filtered = useMemo(
    () =>
      USERS.filter((u) => {
        if (personaFilter !== "all" && !u.personas.some((p) => p.type === personaFilter)) return false;
        if (tierFilter !== "all" && String(maxTierOf(u)) !== tierFilter) return false;
        if (search && !(u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search))) return false;
        return true;
      }),
    [personaFilter, tierFilter, search]
  );

  const openUser = USERS.find((u) => u.id === openUserId) || null;
  const persona = openUser
    ? openUser.personas.find((p) => p.label === activeTab) || openUser.personas[0]
    : null;

  const open = (user) => {
    setOpenUserId(user.id);
    setActiveTab(user.personas[0].label);
  };

  return (
    <>
      <div className="admin-toolbar">
        <Segmented
          name="personaFilter"
          options={PERSONA_FILTERS}
          value={personaFilter}
          onChange={setPersonaFilter}
        />
        <SelectFilter value={tierFilter} onChange={setTierFilter} options={TIER_FILTERS} />
        <div className="admin-spacer" />
        <CountLabel shown={filtered.length} total={USERS.length} noun="accounts" />
      </div>

      <div className="card admin-table-card">
        <table className="table">
          <thead>
            <tr>
              <th className="admin-cell-lead">Name</th>
              <th>Persona(s)</th>
              <th>Highest tier</th>
              <th>Location</th>
              <th>Signed up</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <NoResults colSpan={7} />}
            {filtered.map((u) => {
              const status = USER_STATUS_META[u.status];
              return (
                <tr key={u.id} className="admin-row-link" onClick={() => open(u)}>
                  <td className="admin-cell-lead">
                    <div className="admin-cell-strong">{u.name}</div>
                    <div className="admin-cell-sub">{u.email}</div>
                  </td>
                  <td>
                    <div className="admin-chips">
                      {u.personas.map((p) => (
                        <span className={`tag ${PERSONA_TAG_CLASS[p.type]}`} key={p.label}>
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td><TierMeter tier={maxTierOf(u)} /></td>
                  <td>{u.location}</td>
                  <td className="admin-muted-cell">{u.signup}</td>
                  <td><span className={`tag ${status.tagClass}`}>{status.label}</span></td>
                  <td className="admin-chevron">›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openUser && (
        <Drawer onClose={() => setOpenUserId(null)} labelledBy="user-drawer-title">
          <div className="admin-drawer-head">
            <h4 id="user-drawer-title" style={{ margin: 0 }}>{openUser.name}</h4>
            <CloseButton onClick={() => setOpenUserId(null)} label="Close user detail" />
          </div>
          <div className="admin-drawer-meta">
            {openUser.email} · {openUser.location} · joined {openUser.signup}
          </div>

          <div className="admin-drawer-tabs">
            {openUser.personas.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`btn ${persona.label === p.label ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setActiveTab(p.label)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="card-kicker">Verification</div>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Tier {persona.tier} of 3</span>
            </div>

            <TierBars tier={persona.tier} />

            {persona.missing.length > 0 ? (
              <>
                <div
                  style={{
                    fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase",
                    opacity: 0.55, marginBottom: 6,
                  }}
                >
                  Missing for tier {persona.tier + 1}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {persona.missing.map((m) => (
                    <div key={m} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--status-warn)" }}>
                      {m}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: "var(--status-ok)" }}>Fully verified</div>
            )}
          </div>

          <div className="card">
            <div className="card-kicker">Persona</div>
            <div className="card-title" style={{ fontSize: 15 }}>{persona.label}</div>
            <div className="card-meta">Type: {PERSONA_TYPE_LABEL[persona.type]}</div>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default Users;
